import { defineStore } from 'pinia'
import { klona } from 'klona'
import { makeId, idPrefixes, uuid } from '@dashboard/services/uuid'
import { type Widget, type Flow, widgetSchema } from '@dashboard/types/config'
import { sanitizeEmbedCodeShortname } from '../services/widgetUtilities'
import { funnelDefinitionEach } from '@dashboard/modules/flow/services/funnelDefinitions'
import { useFlowStore } from '@dashboard/modules/flow/stores/flowStore'
import { createWidgetDefaults } from '@dashboard/modules/widget/services/widgetCreation'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import { useConfigStore } from '@dashboard/stores/configStore'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { z } from 'zod'
import { WIDGET_TYPES, withCopiedDesign } from '../data/widgetDefaults'

export const useWidgetStore = defineStore('widget', () => {
  const configStore = useConfigStore()
  const { config } = storeToRefs(configStore)
  const flowStore = useFlowStore()
  const { flows } = storeToRefs(flowStore)

  const widgets = computed(() => config.value?.rightCta?.widgets || [])
  const getById = (id: string) => widgets.value.find((widget) => widget.id === id)

  const getWidgetsForOfferFunnel = (offerFunnelId: string) =>
    widgets.value.filter((widget) => widget.contentId === offerFunnelId)

  const getWidgetsForOffer = (offerId: string) =>
    flows.value
      .filter((flow: Flow) => {
        let found = false
        funnelDefinitionEach(flow.funnelDefinition, (node: any) => {
          if (node.type === 'offer' && node.offerId === offerId) {
            found = true
          }
        })
        return found
      })
      .flatMap((flow: Flow) => flow.id)
      .reduce((uniqueWidgets: Widget[], flowId: string) => {
        const matchingWidgets = widgets.value.filter((widget) => widget.contentId === flowId)
        uniqueWidgets.push(
          ...matchingWidgets.filter(
            (widget) => !uniqueWidgets.some((existingWidget) => existingWidget.id === widget.id)
          )
        )
        return uniqueWidgets
      }, [])

  const create = ({
    type,
    name,
    theme,
    contentId,
    copyOptionsFrom
  }: {
    type?: Widget['type']
    name: string
    theme: string
    contentId: string
    copyOptionsFrom?: Widget['options']
  }) => {
    const creationType = z.enum(WIDGET_TYPES).optional().parse(type)
    const defaultOptions: Widget['options'] = createWidgetDefaults(creationType, theme)

    // Copying from another widget takes its design, never its placement, device targets or theme.
    const options: Widget['options'] = copyOptionsFrom
      ? {
          ...withCopiedDesign(defaultOptions, copyOptionsFrom),
          backButtonLabel: copyOptionsFrom.backButtonLabel,
          backButtonEnabled: copyOptionsFrom.backButtonEnabled
        }
      : defaultOptions

    const newWidget = widgetSchema.parse({
      id: makeId(idPrefixes.WIDGET),
      name,
      type,
      contentId,
      isEnabled: true,
      visibility: {
        conditions: null,
        trigger: 'immediate',
        areas: type === 'inline' ? sanitizeEmbedCodeShortname(name) : undefined
      },
      whenToAskNext: 'startover',
      options
    }) as Widget

    widgets.value.push(newWidget)

    void recordAnalyticsEvent(AnalyticsEventTypes.WIDGET_CREATED, {
      id: newWidget.id,
      type: newWidget.type,
      name: newWidget.name
    })

    return newWidget
  }

  const destroy = (widget: Widget) => {
    const foundIndex = widgets.value.findIndex((w) => w.id === widget.id)
    if (foundIndex === -1) {
      return
    } // this should really never happen

    widgets.value.splice(foundIndex, 1)

    // Track widget deletion
    void recordAnalyticsEvent(AnalyticsEventTypes.WIDGET_DELETED, {
      widgetId: widget.id,
      widgetName: widget.name,
      widgetType: widget.type,
      wasEnabled: widget.isEnabled,
      contentId: widget.contentId,
      triggerType: widget.visibility?.trigger
    })
  }

  const duplicate = (widget: Widget) => {
    const newName = `${widget.name} (Copy)`
    const newWidget = {
      ...klona(widget),
      id: makeId(idPrefixes.WIDGET),
      name: newName,
      // The copy is new: the server stamps it on save instead of inheriting the source's time.
      modifiedAt: undefined,
      // Sanitize the areas field for inline widgets when duplicating
      visibility: {
        ...klona(widget.visibility),
        areas: widget.type === 'inline' ? sanitizeEmbedCodeShortname(newName) : widget.visibility?.areas
      } as Widget['visibility']
    }

    widgets.value.push(newWidget)

    // Track widget duplication
    void recordAnalyticsEvent(AnalyticsEventTypes.WIDGET_CREATED, {
      widgetId: newWidget.id,
      widgetName: newWidget.name,
      widgetType: newWidget.type,
      isEnabled: newWidget.isEnabled,
      contentId: newWidget.contentId,
      triggerType: newWidget.visibility?.trigger,
      isDuplicate: true,
      sourceWidgetId: widget.id,
      sourceWidgetName: widget.name
    })

    return newWidget
  }

  return {
    widgets,
    getById,
    getWidgetsForOfferFunnel,
    getWidgetsForOffer,
    create,
    destroy,
    duplicate
  }
})
